import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

const EFFECTIVE_DATE = "February 2026";

export default function TermsOfService() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-16 tga-fade" data-testid="terms-page">
      <button
        data-testid="terms-back-btn"
        onClick={() => navigate("/")}
        className="ghost-btn rounded-lg px-3 py-1.5 text-sm inline-flex items-center gap-1 mb-6"
      >
        <ChevronLeft size={16} /> Back
      </button>

      <div className="text-center mb-8">
        <img src="/logo-rounded.png" alt="TGA Golf" className="crest crest-img mx-auto mb-4" />
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brass-bright)]">
          Terms of Service
        </h1>
        <p className="mt-2 text-[color:var(--muted-green)] italic font-display">
          Effective {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="tga-card space-y-6 text-[15px] leading-relaxed">
        <section>
          <p>
            Welcome to <strong>TGA Golf</strong> (the "Service"). By accessing or using
            the Service you agree to be bound by these Terms of Service ("Terms"). If
            you do not agree, please do not use the Service.
          </p>
        </section>

        <section>
          <h2 className="section-title">1. Eligibility</h2>
          <p>
            You must be at least 13 years old to use the Service. By using it you
            represent that the information you provide is accurate and that you have
            the authority to enter these Terms.
          </p>
        </section>

        <section>
          <h2 className="section-title">2. Your Account</h2>
          <p>
            You are responsible for activity that happens under your Google account
            while signed in to the Service. Keep your credentials secure and notify us
            of any unauthorized use.
          </p>
        </section>

        <section>
          <h2 className="section-title">3. Trips, Roles, and Content</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>The person who creates a trip is its "organizer" and can edit setup, players, and courses.</li>
            <li>Any player with the trip code can view scores and enter their own results.</li>
            <li>You are responsible for the accuracy of the information you enter.</li>
            <li>You must not upload or enter content that is unlawful, hateful, or infringes another's rights.</li>
          </ul>
        </section>

        <section>
          <h2 className="section-title">4. Side Games and Money</h2>
          <p>
            Side games (Skins, Nassau, BBB, Champion Points) are informal, player-run
            wagers between participants. TGA Golf only calculates and displays
            suggested payouts based on the scores you enter — it does not process,
            hold, or transfer any money. All settlement is strictly between the
            players themselves.
          </p>
        </section>

        <section>
          <h2 className="section-title">5. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Interfere with, disrupt, or attempt to gain unauthorized access to the Service.</li>
            <li>Scrape, reverse engineer, or misuse the Service or its APIs.</li>
            <li>Impersonate another player or misrepresent your affiliation.</li>
          </ul>
        </section>

        <section>
          <h2 className="section-title">6. Third-Party Services</h2>
          <p>
            The Service uses Google for sign-in and GolfCourseAPI for course data.
            Your use of those services is subject to their own terms and privacy
            policies.
          </p>
        </section>

        <section>
          <h2 className="section-title">7. Intellectual Property</h2>
          <p>
            The TGA Golf name, crest, design, and software are owned by us. You may
            not copy, modify, distribute, or create derivative works from the Service
            without our written permission. You retain ownership of the trip and
            scoring data you enter.
          </p>
        </section>

        <section>
          <h2 className="section-title">8. Disclaimers</h2>
          <p>
            The Service is provided "as is" and "as available", without warranties of
            any kind, express or implied, including merchantability, fitness for a
            particular purpose, and non-infringement. We do not guarantee that scores,
            leaderboards, or payouts are error-free.
          </p>
        </section>

        <section>
          <h2 className="section-title">9. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, TGA Golf shall not be liable for
            any indirect, incidental, special, consequential, or punitive damages, or
            any loss of profits or revenues, arising out of your use of the Service.
          </p>
        </section>

        <section>
          <h2 className="section-title">10. Termination</h2>
          <p>
            We may suspend or terminate access to the Service at any time if you
            violate these Terms. You may stop using the Service at any time.
          </p>
        </section>

        <section>
          <h2 className="section-title">11. Changes</h2>
          <p>
            We may update these Terms from time to time. Material changes will be
            reflected by updating the effective date above. Continued use of the
            Service after changes take effect constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 className="section-title">12. Contact</h2>
          <p>
            Questions about these Terms? Email{" "}
            <a
              className="underline text-[color:var(--brass-dark)]"
              href="mailto:privacy@tgagolf.com"
              data-testid="terms-contact-email"
            >
              privacy@tgagolf.com
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
